import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ResetPasswordComponent,
    useAuth,
    signUpWithEmailAndPassword,
    useSetTokensWithRedirect,
} from '@lendica/auth';
import type { ResetPasswordSubmitHandler, SignUpUtmMeta } from '@lendica/auth';
import { useLeadData } from '@lendica/application-form';

export const SignupCreatePasswordView = ({
    onAuthSuccess,
}: {
    onAuthSuccess?: (tokens?: any) => void;
}) => {
    const navigate = useNavigate();
    const { setError, setSuccessMsg } = useAuth();
    const { leadData, consumeLeadData } = useLeadData();
    const [initialized, setInitialized] = useState(false);
    const setTokensWithRedirect = useSetTokensWithRedirect(onAuthSuccess);

    useEffect(() => {
        // Check if we have lead data
        if (!leadData || !('email' in leadData)) {
            // No lead data, redirect to signup start
            navigate('/signup', { replace: true });
            return;
        }

        setError(null);
        setSuccessMsg('Almost there! Create a password to secure your account.');
        setInitialized(true);
    }, [leadData, navigate]);

    const handleSubmit: ResetPasswordSubmitHandler = async (values, { setSubmitting }) => {
        if (!leadData || !('email' in leadData)) {
            setError('Missing signup information. Please start over.');
            return;
        }

        try {
            setSubmitting(true);
            setSuccessMsg(null);
            setError(null);

            const leadInfo = leadData as Record<string, unknown>;

            const getString = (key: string) => {
                const value = leadInfo[key];
                return typeof value === 'string' ? value.trim() : '';
            };

            const email = getString('email');
            const firstName = getString('firstName');
            const lastName = getString('lastName');
            const companyName = getString('company');
            const phoneNumber = getString('phone') || undefined;

            const missingRequired = [
                ['email', email],
                ['first name', firstName],
                ['last name', lastName],
                ['company name', companyName],
            ].filter(([, value]) => !value);

            if (missingRequired.length) {
                setSubmitting(false);
                setError('Missing signup information. Please start over.');
                return;
            }

            // Prepare UTM meta from lead data
            const utmMeta: SignUpUtmMeta = {};

            // Extract UTM and other allowed params from lead data
            Object.entries(leadInfo).forEach(([key, value]) => {
                if (
                    key.startsWith('utm_') ||
                    key === 'partner_name' ||
                    key === 'source' ||
                    key === 'medium' ||
                    key === 'campaign' ||
                    key === 'coupon_code'
                ) {
                    if (typeof value === 'string' && value.trim()) {
                        utmMeta[key] = value.trim();
                    }
                }
            });

            // Add goals if present
            const goalsValue = leadInfo['goals'];
            if (Array.isArray(goalsValue)) {
                const joinedGoals = goalsValue.filter(Boolean).join(',');
                if (joinedGoals) {
                    utmMeta.goals = joinedGoals;
                }
            }

            const experimentsRaw = leadInfo['experiments'];

            const isExperimentMeta = (
                value: unknown
            ): value is NonNullable<SignUpUtmMeta['experiments']>[number] => {
                if (!value || typeof value !== 'object') return false;
                const experiment = value as Record<string, unknown>;
                return (
                    typeof experiment.feature_flag_name === 'string' &&
                    typeof experiment.variant === 'string' &&
                    typeof experiment.recorded_at === 'string' &&
                    typeof experiment.source === 'string'
                );
            };

            const parseExperiments = (input: unknown): SignUpUtmMeta['experiments'] => {
                if (!input) return undefined;

                let rawValue = input;
                if (typeof rawValue === 'string') {
                    try {
                        rawValue = JSON.parse(rawValue);
                    } catch (error) {
                        console.warn('Unable to parse experiments JSON', error);
                        return undefined;
                    }
                }

                if (!Array.isArray(rawValue)) return undefined;

                const sanitized = rawValue.filter(isExperimentMeta);
                return sanitized.length ? (sanitized as SignUpUtmMeta['experiments']) : undefined;
            };

            const parsedExperiments = parseExperiments(experimentsRaw);
            if (parsedExperiments) {
                utmMeta.experiments = parsedExperiments;
            }

            if (!utmMeta.goals && typeof goalsValue === 'string' && goalsValue.trim()) {
                utmMeta.goals = goalsValue.trim();
            }

            if (!utmMeta.partner_name) {
                const partnerName =
                    (leadInfo['partner_name'] as string | undefined) ??
                    (leadInfo['partnerName'] as string | undefined);
                if (partnerName?.trim()) {
                    utmMeta.partner_name = partnerName.trim();
                }
            }

            if (!utmMeta.coupon_code) {
                const couponCode =
                    (leadInfo['coupon_code'] as string | undefined) ??
                    (leadInfo['promo_code'] as string | undefined) ??
                    (leadInfo['referral_code'] as string | undefined);
                if (couponCode?.trim()) {
                    utmMeta.coupon_code = couponCode.trim();
                }
            }

            // Call signup API
            const response = await signUpWithEmailAndPassword(
                email,
                values.password,
                values.confirmPassword,
                firstName,
                lastName,
                companyName,
                phoneNumber,
                Object.keys(utmMeta).length ? utmMeta : undefined
            );

            setSubmitting(false);

            // Check for error response
            if ('message' in response && !('access' in response)) {
                setError(response.message);
                return;
            }

            // Clear lead data now that account is created
            consumeLeadData();

            setTokensWithRedirect({
                access: response.access,
                refresh: response.refresh,
            });
        } catch (error: any) {
            setSubmitting(false);
            setSuccessMsg(null);
            console.log(error);
            setError(
                error.response?.data?.message ||
                    error.message ||
                    'Something went wrong. Please try again.'
            );
        }
    };

    if (!initialized) {
        return null;
    }

    return (
        <ResetPasswordComponent
            heading="Create your password"
            body="You're almost done! Set a password to secure your account."
            action="Create account"
            handleSubmit={handleSubmit}
        />
    );
};
